import { config } from '@iheartjane/config';
import {
  GEOCODE_ZERO_RESULTS,
  geocode,
  googleMapsLoader,
} from '@iheartjane/geolocation';
import type { GeocodingResult } from '@iheartjane/geolocation';
import { useEffect, useState } from 'react';
import type { DeepReadonly } from 'ts-essentials';

import { useCartActions } from '@jane/monolith/components/cart/cartFacade/useCartActions';
import { DeliveryValidateSource } from '@jane/monolith/sources/deliveryValidate';
import { reservationModeLabel } from '@jane/monolith/utils/store';
/* eslint-disable-next-line no-restricted-imports */
import type { Store } from '@jane/shared/models';
import type { Address, DeliveryValidation } from '@jane/shared/types';

interface UseValidateDeliveryAddress {
  address: string;
  skip?: boolean;
  store: DeepReadonly<Store>;
  street2?: string | null;
}

interface ValidateDeliveryAddress {
  addressErrorMessage: string | null;
  deliveryValidationData: DeliveryValidation | null;
  googleValidatedAddress: GeocodingResult | null;
  resetAddressValidation: () => void;
}

const buildUndeliverableValidation = (message: string): DeliveryValidation => ({
  can_deliver: false,
  delivers_today: null,
  fee: null,
  last_call_minutes: null,
  lead_time_minutes: null,
  message,
  minimum: null,
});

export const useValidateDeliveryAddress = ({
  address,
  store,
  street2,
  skip,
}: UseValidateDeliveryAddress): ValidateDeliveryAddress => {
  const { updateDeliveryAddress } = useCartActions();
  const [message, setMessage] = useState<string | null>(null);
  const [validationData, setValidationData] =
    useState<DeliveryValidation | null>(null);
  const [googlePlaceId, setGooglePlaceId] = useState<string | null>(null);
  const [googleValidatedAddress, setGoogleValidatedAddress] =
    useState<GeocodingResult | null>(null);

  const resetAddressValidation = () => {
    setValidationData(null);
    setMessage(null);
  };

  useEffect(() => {
    if (skip) return;
    if (!address) return;
    if (address.length < 6) {
      setValidationData(
        buildUndeliverableValidation('Please enter a valid address.')
      );
      setMessage('Please enter a valid address.');
      return;
    }

    const validateAddress = async () => {
      const location = await geocode({
        loader: () => googleMapsLoader(config.googlePlacesKey),
        searchCriteria: address,
      });
      const { zipcode, street, city, state } = location;

      if (location.googlePlaceId === googlePlaceId) return;
      resetAddressValidation();
      setGooglePlaceId(location.googlePlaceId ?? null);

      if (!street || !city || !state || !zipcode) {
        setValidationData(
          buildUndeliverableValidation(
            "We don't service this address. Please confirm the spelling."
          )
        );
        setMessage(
          "We don't service this address. Please confirm the spelling."
        );
        return;
      }

      setGoogleValidatedAddress(location);

      const newValidation = await DeliveryValidateSource.get({
        storeId: store.id,
        zipcode: location?.zipcode,
        coordinates: location?.coordinates,
      });

      setValidationData(newValidation);

      if (!newValidation.can_deliver) {
        setMessage(
          `Outside of ${reservationModeLabel(store, 'delivery')} range`
        );
        return;
      }
      setMessage(null);

      const deliveryAddress: Address = {
        street: street || null,
        street2,
        city: city || null,
        country_code: location.countryCode || null,
        state_code: state || null,
        zipcode: zipcode || null,
        lat: location.coordinates.lat,
        lng: location.coordinates.long,
      };

      // ???: What does this redux thing do? How can I find the subscribers to this action?
      updateDeliveryAddress({ deliveryAddress });
    };

    validateAddress().catch((err) => {
      switch (err.message) {
        case GEOCODE_ZERO_RESULTS: {
          setValidationData(
            buildUndeliverableValidation(
              'We can’t find this address. Make sure it is spelled correctly or try adding a city, state, or zip code.'
            )
          );
          return setMessage(
            'We can’t find this address. Make sure it is spelled correctly or try adding a city, state, or zip code.'
          );
        }
        default: {
          setValidationData(
            buildUndeliverableValidation('Unable to verify address')
          );
          return setMessage('Unable to verify address');
        }
      }
    });
  }, [address, updateDeliveryAddress, googlePlaceId, skip, store, street2]);

  return {
    addressErrorMessage: message,
    deliveryValidationData: validationData,
    googleValidatedAddress: googleValidatedAddress,
    resetAddressValidation,
  };
};
